import axios from 'axios';
import { message } from 'antd';

// 获取后端基础地址
function getBackendBaseUrl() {
  // 优先使用全局配置（运行时注入）
  if (window.__APP_CONFIG__ && window.__APP_CONFIG__.API_URL) {
    return window.__APP_CONFIG__.API_URL;
  }
  
  // 使用环境变量（构建时注入）
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // 使用相对路径，让浏览器自动解析主机和端口
  return ''; // 空字符串表示相对于当前域名
}

// 获取后端API地址
function getApiBaseUrl() {
  return getBackendBaseUrl() + '/api/v1';
}

// 获取图片完整URL
function getImageUrl(fileUrl) {
  if (!fileUrl) return '';
  
  // 如果已经是完整的URL，直接返回
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    return fileUrl;
  }
  
  // 构建完整的图片URL，使用后端API地址
  const backendBaseUrl = getBackendBaseUrl();
  
  // 确保路径格式正确
  let normalizedUrl = fileUrl;
  if (!normalizedUrl.startsWith('/')) {
    normalizedUrl = '/' + normalizedUrl;
  }
  
  // 如果已经包含 /uploads 前缀，直接使用
  if (normalizedUrl.startsWith('/uploads')) {
    return backendBaseUrl + normalizedUrl;
  }
  
  // 否则，添加 /uploads 前缀
  return backendBaseUrl + '/uploads' + normalizedUrl;
}

// 创建axios实例
const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 300000,  // 增加到5分钟，用于处理大量图片上传
  headers: {
    'Content-Type': 'application/json',
  },
});

export { getImageUrl };

/** 从 axios 错误响应中解析可展示的错误文案 */
export function getApiErrorMessage(error, fallback = '操作失败') {
  const data = error?.response?.data;
  if (!data) {
    return error?.message || fallback;
  }
  const detail = data.detail ?? data.message ?? data.error;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((item) => {
        if (typeof item === 'object' && item !== null) {
          return item.msg || item.message || JSON.stringify(item);
        }
        return String(item);
      })
      .join('; ');
  }
  return fallback;
}

/** 页面跳转、登出、组件卸载时 axios 会 abort，非业务错误 */
export function isRequestAborted(error) {
  if (!error) return false;
  if (error.code === 'ERR_CANCELED') return true;
  const msg = String(error.message || '').toLowerCase();
  return msg.includes('aborted') || msg.includes('canceled');
}

function redirectToLogin() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  // HashRouter：必须用 #/login，/login 会整页重载并 abort 进行中的请求
  const loginHash = '#/login';
  if (window.location.hash !== loginHash) {
    window.location.replace(`${window.location.pathname}${window.location.search}${loginHash}`);
  }
}

// 请求拦截器
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Token过期，尝试刷新
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${getApiBaseUrl()}/auth/refresh`, {
            refresh_token: refreshToken,
          });
          
          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);
          
          // 重试原请求
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
        redirectToLogin();
        return Promise.reject(error);
      } catch (refreshError) {
        redirectToLogin();
        return Promise.reject(refreshError);
      }
    }

    if (isRequestAborted(error)) {
      return Promise.reject(error);
    }

    // 处理其他错误
    if (error.response?.status === 403) {
      message.error('权限不足');
    } else if (error.response?.status === 404) {
      message.error('资源不存在');
    } else if (error.response?.status >= 500) {
      const detail = error.response?.data?.detail;
      const hasDetail =
        (typeof detail === 'string' && detail.trim()) ||
        (Array.isArray(detail) && detail.length > 0);
      if (!hasDetail) {
        message.error('服务器错误，请稍后重试');
      }
    }

    return Promise.reject(error);
  }
);

export default api;
